import { 
  ApiStats, 
  ApiToken, 
  User, 
  TldInfo, 
  HealthStatus, 
  ApiActivity,
  WhoisLookupResult,
  LoginCredentials,
  AuthResponse,
  ApiError
} from '../types';
import config from '../config';

// Get API URL dynamically to ensure environment variables are read
const getApiUrl = () => {
  const url = process.env.REACT_APP_API_URL || config.apiUrl;
  console.log('🔧 Using API URL:', url);
  return url;
};

class ApiService {
  private token: string | null = null;

  constructor() {
    // Try to get token from localStorage
    this.token = localStorage.getItem('auth_token') || localStorage.getItem('token');
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    return headers;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      let errorData: ApiError;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = { 
          message: 'An unknown error occurred', 
          status: response.status 
        };
      }
      throw errorData;
    }

    return await response.json() as T;
  }

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const API_URL = getApiUrl();
    console.log('🔐 Attempting login with:', { username: credentials.username, apiUrl: API_URL });
    
    const formData = new FormData();
    formData.append('username', credentials.username);
    formData.append('password', credentials.password);

    const loginUrl = `${API_URL}/api/auth/login`;
    console.log('🌐 Login URL:', loginUrl);

    try {
      const response = await fetch(loginUrl, {
        method: 'POST',
        body: formData
      });

      console.log('📡 Response status:', response.status);
      console.log('📡 Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Login failed:', response.status, errorText);
        throw new Error(`Login failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ Login successful, token received');
      
      this.token = data.access_token;
      localStorage.setItem('auth_token', data.access_token);
      
      // Return in the format expected by the frontend
      return { 
        token: data.access_token, 
        user: { 
          id: '', 
          username: credentials.username, 
          email: '', 
          role: 'admin', 
          created_at: '', 
          last_login: null 
        } 
      };
    } catch (error) {
      console.error('❌ Login error:', error);
      throw error;
    }
  }

  logout(): void {
    this.token = null;
    localStorage.removeItem('auth_token');
    localStorage.removeItem('token');
  }

  async getCurrentUser(): Promise<User> {
    const API_URL = getApiUrl();
    const response = await fetch(`${API_URL}/api/users/me`, {
      headers: this.getHeaders()
    });
    return await this.handleResponse<User>(response);
  }

  async getStats(): Promise<ApiStats> {
    const API_URL = getApiUrl();
    const response = await fetch(`${API_URL}/api/stats`, {
      headers: this.getHeaders()
    });
    return await this.handleResponse<ApiStats>(response);
  }

  async getHealthStatus(): Promise<HealthStatus> {
    const API_URL = getApiUrl();
    const response = await fetch(`${API_URL}/api/health`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        // Remove authorization for health check as it should be public
      }
    });
    return await this.handleResponse<HealthStatus>(response);
  }

  async getTokens(): Promise<ApiToken[]> {
    const API_URL = getApiUrl();
    const response = await fetch(`${API_URL}/api/tokens`, {
      headers: this.getHeaders()
    });
    return await this.handleResponse<ApiToken[]>(response);
  }

  async createToken(tokenData: {
    name: string;
    hourly_limit?: number | null;
    daily_limit?: number | null;
    is_unlimited?: boolean;
  }): Promise<ApiToken> {
    const API_URL = getApiUrl();
    const response = await fetch(`${API_URL}/api/tokens`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(tokenData)
    });
    return await this.handleResponse<ApiToken>(response);
  }

  async updateToken(id: string, updates: {
    name?: string;
    hourly_limit?: number | null;
    daily_limit?: number | null;
    is_unlimited?: boolean;
    is_active?: boolean;
  }): Promise<void> {
    const API_URL = getApiUrl();
    const response = await fetch(`${API_URL}/api/tokens/${id}`, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify(updates)
    });
    await this.handleResponse<void>(response);
  }

  async deleteToken(id: string): Promise<void> {
    const API_URL = getApiUrl();
    const response = await fetch(`${API_URL}/api/tokens/${id}`, {
      method: 'DELETE',
      headers: this.getHeaders()
    });
    await this.handleResponse<void>(response);
  }

  async getTokenUsage(id: string): Promise<{
    token_name: string;
    created_at: string;
    hourly_limit: number | null;
    daily_limit: number | null;
    is_unlimited: boolean;
    total_usage: number;
    hourly_usage: number;
    daily_usage: number;
    last_usage: string | null;
    daily_breakdown: Array<{date: string, count: number}>;
  }> {
    const API_URL = getApiUrl();
    const response = await fetch(`${API_URL}/api/tokens/${id}/usage`, {
      headers: this.getHeaders()
    });
    return await this.handleResponse<any>(response);
  }

  async getUsers(): Promise<User[]> {
    const API_URL = getApiUrl();
    const response = await fetch(`${API_URL}/api/users`, {
      headers: this.getHeaders()
    });
    return await this.handleResponse<User[]>(response);
  }

    async createUser(user: Omit<User, 'id' | 'created_at' | 'last_login'> & { password: string }): Promise<User> {
    const API_URL = getApiUrl();
    const response = await fetch(`${API_URL}/api/users`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(user)
    });
    return await this.handleResponse<User>(response);
  }

  async updateUser(id: string, userData: Partial<User> & { password?: string }): Promise<User> {
    const API_URL = getApiUrl();
    const response = await fetch(`${API_URL}/api/users/${id}`, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify(userData)
    });
    return await this.handleResponse<User>(response);
  }

  async deleteUser(id: string): Promise<void> {
    const API_URL = getApiUrl();
    const response = await fetch(`${API_URL}/api/users/${id}`, {
      method: 'DELETE',
      headers: this.getHeaders()
    });
    await this.handleResponse<void>(response);
  }

  async getTlds(): Promise<TldInfo[]> {
    const API_URL = getApiUrl();
    const response = await fetch(`${API_URL}/api/tlds`, {
      headers: this.getHeaders()
    });
    return await this.handleResponse<TldInfo[]>(response);
  }

  async createTld(tldData: {
    tld: string;
    supported_protocol?: string;
    server?: string;
    rdap_server?: string;
    is_active?: boolean;
  }): Promise<void> {
    const API_URL = getApiUrl();
    const response = await fetch(`${API_URL}/api/tlds`, {
      method: 'POST', 
      headers: this.getHeaders(),
      body: JSON.stringify(tldData)
    });
    await this.handleResponse<void>(response);
  }

  async updateTld(tld: string, updates: {
    supported_protocol?: string;
    server?: string;
    rdap_server?: string;
    is_active?: boolean;
  }): Promise<void> {
    const API_URL = getApiUrl();
    const response = await fetch(`${API_URL}/api/tlds/${tld}`, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify(updates)
    });
    await this.handleResponse<void>(response);
  }

  async getActivities(limit: number = 10): Promise<ApiActivity[]> {
    const API_URL = getApiUrl();
    const response = await fetch(`${API_URL}/api/activities?limit=${limit}`, {
      headers: this.getHeaders()
    });
    return await this.handleResponse<ApiActivity[]>(response);
  }

  async lookupDomain(domain: string): Promise<WhoisLookupResult> {
    const API_URL = getApiUrl();
    const response = await fetch(`${API_URL}/api/lookup?domain=${encodeURIComponent(domain)}`, {
      headers: this.getHeaders()
    });
    return await this.handleResponse<WhoisLookupResult>(response);
  }
}

const apiService = new ApiService();
export default apiService; 